import { keepPreviousData, useMutation, useQuery } from '@tanstack/react-query';
import { Component, lazy, Suspense, useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import { supabase } from '../lib/supabase.ts';
import { Betolto, Gomb, HibaUzenet } from '../ui/elemek.tsx';
import {
  ALAP_SUGAR_KM, csuszkaHatar, kezdoKozeppont, kivetelValtas, korzetLista, korzetMentesTorzs, osszesito, osszesLap, tuKorlat,
  type Kivetelek, type TeruletSor,
} from './korzetSzerkeszto.ts';

// A Leaflet csak az irodaszerkesztőn töltődik, külön chunkban.
const Terkep = lazy(() => import('./Terkep.tsx'));

const KESLELTETES_MS = 300;

function useKesleltetett<T>(ertek: T, ms: number): T {
  const [k, setK] = useState(ertek);
  useEffect(() => {
    const t = setTimeout(() => setK(ertek), ms);
    return () => clearTimeout(t);
  }, [ertek, ms]);
  return k;
}

// Ha a térkép (a chunk vagy a Leaflet) nem tölt be, a blokk térkép nélkül is használható.
class TerkepHatar extends Component<{ children: ReactNode }, { hiba: boolean }> {
  state = { hiba: false };
  static getDerivedStateFromError() { return { hiba: true }; }
  render() {
    return this.state.hiba
      ? <p role="status" className="rounded-xl border border-vonal px-3.5 py-2.5 text-sm text-szoveg-halk">A térkép nem töltött be. A sugár a csúszkával állítható, a kivételek a listából kapcsolhatók; a tű helye ilyenkor nem módosítható.</p>
      : this.props.children;
  }
}

const tizedes = (n: number) => n.toFixed(1).replace('.', ',');
const ALLAPOT: Record<string, string> = { be: 'kézzel bevéve', ki: 'kézzel kivéve' };

/** Az iroda szolgáltatási körzete: tű, sugár és kézi kivételek. Csak a super admin látja; a mentést az RPC is csak neki engedi. */
export default function KorzetBlokk({ officeId, varos }: { officeId: string; varos: string | null }) {
  const betoltes = useQuery({
    queryKey: ['super-korzet', officeId],
    // Mindig friss állapotból indul: a belső szerkesztő csak egyszer veszi át.
    gcTime: 0,
    refetchOnWindowFocus: false,
    queryFn: async () => {
      const { data: iroda, error } = await supabase.from('offices').select('korzet_lat, korzet_lon, korzet_sugar_km').eq('id', officeId).single();
      if (error) throw error;
      const { data: kiv, error: kivHiba } = await supabase
        .from('korzet_kivetelek').select('terulet_kod, mod, teruletek(kod, nev, tipus, lat, lon)').eq('office_id', officeId);
      if (kivHiba) throw kivHiba;
      let egyezo: { lat: number | null; lon: number | null }[] = [];
      if (iroda.korzet_lat === null && varos) {
        const { data, error: egyHiba } = await supabase.from('teruletek').select('lat, lon').eq('nev', varos).not('lat', 'is', null).limit(2);
        if (egyHiba) throw egyHiba;
        egyezo = data;
      }
      const kivetelek: Record<string, 'be' | 'ki'> = {};
      const tovabbi: TeruletSor[] = [];
      for (const k of kiv) {
        kivetelek[k.terulet_kod] = k.mod === 'be' ? 'be' : 'ki';
        if (k.teruletek) tovabbi.push(k.teruletek);
      }
      return { iroda, kivetelek, tovabbi, egyezo };
    },
  });

  if (betoltes.isPending) return <Betolto />;
  if (betoltes.error) return <HibaUzenet hiba={betoltes.error} />;
  const { iroda, kivetelek, tovabbi, egyezo } = betoltes.data;
  return (
    <KorzetSzerkeszto
      officeId={officeId}
      kezdetiKorzet={iroda.korzet_lat !== null && iroda.korzet_lon !== null && iroda.korzet_sugar_km !== null
        ? { lat: iroda.korzet_lat, lon: iroda.korzet_lon, sugarKm: iroda.korzet_sugar_km } : null}
      kezdoKozeppont={kezdoKozeppont({ lat: iroda.korzet_lat, lon: iroda.korzet_lon }, egyezo)}
      kezdetiKivetelek={kivetelek}
      mentettTeruletek={tovabbi}
    />
  );
}

function KorzetSzerkeszto({ officeId, kezdetiKorzet, kezdoKozeppont: kezdo, kezdetiKivetelek, mentettTeruletek }: {
  officeId: string;
  kezdetiKorzet: { lat: number; lon: number; sugarKm: number } | null;
  kezdoKozeppont: { lat: number; lon: number };
  kezdetiKivetelek: Kivetelek;
  mentettTeruletek: TeruletSor[];
}) {
  const [nincs, setNincs] = useState(kezdetiKorzet === null);
  const [kozeppont, setKozeppont] = useState(kezdetiKorzet ?? kezdo);
  const [sugarKm, setSugarKm] = useState(kezdetiKorzet?.sugarKm ?? ALAP_SUGAR_KM);
  const [kivetelek, setKivetelek] = useState<Kivetelek>(kezdetiKivetelek);
  const [mentve, setMentve] = useState(false);
  // A csúszka határai a betöltött sugárhoz igazodnak, és szerkesztés közben nem ugrálnak.
  const [hatar] = useState(() => csuszkaHatar(kezdetiKorzet?.sugarKm ?? ALAP_SUGAR_KM));

  // A területlista kérése a tű- és csúszkaváltás után késleltetve indul; a régi kérés kulcsa eldobódik.
  const keres = useKesleltetett({ lat: kozeppont.lat, lon: kozeppont.lon, sugarKm }, KESLELTETES_MS);
  const teruletek = useQuery({
    queryKey: ['korzet-teruletek', keres.lat, keres.lon, keres.sugarKm],
    enabled: !nincs,
    placeholderData: keepPreviousData,
    queryFn: async () => {
      // A `max_rows` 1000 sor felett némán levágna (Budapest körül 80 + 25 km-nél több terület jöhet): lapozunk.
      return osszesLap(async (tol, ig) => {
        const { data, error } = await supabase.rpc('korzet_teruletek', { p_lat: keres.lat, p_lon: keres.lon, p_km: keres.sugarKm }).range(tol, ig);
        if (error) throw error;
        return data;
      });
    },
  });
  // Minden eddig látott terület: a tű mozgatása közben a kivétel-váltás elemei nem tűnhetnek el.
  const ismert = useRef(new Map<string, TeruletSor>(mentettTeruletek.map((t) => [t.kod, t])));
  if (teruletek.data) for (const t of teruletek.data) ismert.current.set(t.kod, t);

  const lista = useMemo(
    () => korzetLista(teruletek.data ?? [], { ...kozeppont, sugarKm }, kivetelek, [...ismert.current.values()]),
    [teruletek.data, kozeppont, sugarKm, kivetelek],
  );
  const ossz = osszesito(lista);
  // Mentés csak akkor, ha a lista a jelenlegi tűhöz és sugárhoz tartozik: különben egy frissen szűkített körben a
  // fölöslegessé vált kivételek szűrése téves adatból menne.
  const naprakesz = !teruletek.isPlaceholderData && !teruletek.isFetching
    && keres.lat === kozeppont.lat && keres.lon === kozeppont.lon && keres.sugarKm === sugarKm;

  const mentes = useMutation({
    mutationFn: async () => {
      const torzs = korzetMentesTorzs(nincs ? null : { ...kozeppont, sugarKm }, lista);
      const { error } = await supabase.rpc('korzet_mentes', { p_office_id: officeId, ...torzs });
      if (error) throw error;
      return torzs;
    },
    onSuccess: (torzs) => {
      // A mentés szűrte ki a fölöslegessé vált kivételeket; a képernyő is ezt mutassa.
      setKivetelek(Object.fromEntries(torzs.p_kivetelek.map((k) => [k.terulet_kod, k.mod])));
      setMentve(true);
    },
  });

  const valtozott = () => { setMentve(false); mentes.reset(); };

  return (
    <section className="grid gap-4 px-5 py-5" aria-labelledby="korzet-cim">
      <h2 id="korzet-cim" className="text-lg font-medium tracking-[-0.015em]">Körzet</h2>
      <p className="text-sm text-szoveg-halk">Az iroda tűjétől számított sugár; a körzeten kívüli címnél a látogató megáll a varázslóban. Egy-egy kerületet vagy települést kézzel is be- vagy kivehetsz.</p>
      <label className="flex min-h-11 items-center gap-3">
        <input type="checkbox" className="size-5 accent-kiemelt" checked={nincs} onChange={(e) => { valtozott(); setNincs(e.target.checked); }} />
        Nincs körzet (nem szűr)
      </label>

      {!nincs && (
        <>
          <TerkepHatar>
            <Suspense fallback={<div className="grid h-72 place-items-center rounded-xl border border-vonal sm:h-96"><Betolto /></div>}>
              <Terkep
                kozeppont={kozeppont}
                sugarKm={sugarKm}
                lista={lista}
                onTuMozgott={(lat, lon) => { valtozott(); setKozeppont(tuKorlat(lat, lon)); }}
                onKivetelValtas={(kod) => { const e = lista.find((x) => x.kod === kod); if (e) { valtozott(); setKivetelek((k) => kivetelValtas(k, e)); } }}
              />
            </Suspense>
          </TerkepHatar>
          <p className="text-sm text-szoveg-halk">A tű húzható, vagy kattints a térképre az új helyére. A pontra kattintva kivételt kapcsolsz.</p>

          <div className="grid gap-1.5">
            <label htmlFor="korzet-sugar" className="text-sm text-szoveg-halk">Körzet sugara: <strong className="text-szoveg">{sugarKm} km</strong></label>
            <input
              id="korzet-sugar" type="range" className="min-h-11 w-full text-[max(16px,1rem)] accent-kiemelt"
              min={hatar.min} max={hatar.max} step={hatar.lepes} value={sugarKm}
              onChange={(e) => { valtozott(); setSugarKm(Number(e.target.value)); }}
            />
          </div>

          <div className="grid gap-2">
            <p role="status" className="font-medium">Benne: {ossz.kerulet} kerület, {ossz.telepules} település</p>
            {teruletek.error && <HibaUzenet hiba={teruletek.error} />}
            {lista.length === 0 && !teruletek.isPending && <p className="text-sm text-szoveg-halk">A körzet környékén nincs koordinátás terület.</p>}
            <ul className="grid max-h-80 gap-1 overflow-y-auto" aria-label="Területek a körzet környékén">
              {lista.map((e) => (
                <li key={e.kod}>
                  <button
                    type="button" aria-pressed={e.kivetel !== null}
                    onClick={() => { valtozott(); setKivetelek((k) => kivetelValtas(k, e)); }}
                    className={`flex min-h-11 w-full items-center justify-between gap-3 rounded-xl border px-3.5 text-left text-sm hover:bg-felulet ${e.kivetel ? 'border-vonal-eros border-dashed' : 'border-vonal'}`}
                  >
                    <span className={e.vegso ? 'font-medium' : 'text-szoveg-halk line-through'}>{e.nev}</span>
                    <span className="shrink-0 text-szoveg-halk">
                      {e.tavolsagKm === null ? 'távolság ismeretlen' : `${tizedes(e.tavolsagKm)} km`}
                      {' · '}{e.vegso ? 'benne' : 'kívül'}{e.kivetel ? ` (${ALLAPOT[e.kivetel]})` : ''}
                    </span>
                  </button>
                </li>
              ))}
            </ul>
          </div>
        </>
      )}

      <HibaUzenet hiba={mentes.error} />
      {mentve && <p role="status" className="rounded-xl border border-siker px-3.5 py-2.5 text-sm text-siker">Körzet mentve.</p>}
      <div>
        <Gomb toltes={mentes.isPending} disabled={!nincs && !naprakesz} onClick={() => mentes.mutate()}>Körzet mentése</Gomb>
      </div>
    </section>
  );
}
